import { type ReactNode, useState } from "react";
import { Button, DatePicker, Modal, Switch } from "antd";
import clsx from "clsx";
import dayjs from "dayjs";
import { CloseIcon, EqualizerIcon } from "@/shared/assets/svg/icons";
import "./journal-filters-menu.scss";

export type FilterOption = { id: string; label: string };

type JournalFiltersMenuProps = {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;
  withDividers?: boolean;
  children: ReactNode;
};

export const JournalFiltersMenu = ({
  open,
  onClose,
  onApply,
  onReset,
  withDividers = true,
  children,
}: JournalFiltersMenuProps) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={null}
    width={801}
    centered
    destroyOnClose={false}
    className={clsx("journal-filters-menu", !withDividers && "journal-filters-menu--no-dividers")}
    closeIcon={
      <span className="journal-filters-menu__close">
        <CloseIcon />
      </span>
    }
    title={
      <div className="journal-filters-menu__title">
        <span>Фильтры</span>
        <EqualizerIcon />
      </div>
    }
  >
    <div className="journal-filters-menu__body">{children}</div>
    <div className="journal-filters-menu__footer">
      <div className="journal-filters-menu__actions">
        <Button type="primary" htmlType="button" onClick={onApply}>
          Применить фильтры
        </Button>
        <Button htmlType="button" className="journal-filters-menu__reset" onClick={onReset}>
          Сбросить
        </Button>
      </div>
    </div>
  </Modal>
);

export const FiltersMenuRow = ({
  label,
  children,
  onClear,
  stacked = false,
  withDivider = true,
}: {
  label: string;
  children: ReactNode;
  onClear?: () => void;
  stacked?: boolean;
  withDivider?: boolean;
}) => {
  const rowClassName = clsx(
    "journal-filters-menu__row",
    stacked && "journal-filters-menu__row--stacked",
    withDivider && "journal-filters-menu__row--with-divider",
  );

  if (stacked) {
    return (
      <div className={rowClassName}>
        <div className="journal-filters-menu__row-header">
          <span className="journal-filters-menu__row-label">{label}</span>
          {onClear ? (
            <button type="button" className="journal-filters-menu__clear-all" onClick={onClear}>
              Сбросить все
            </button>
          ) : null}
        </div>
        <div className="journal-filters-menu__row-content">{children}</div>
      </div>
    );
  }

  return (
    <div className={rowClassName}>
      <span className="journal-filters-menu__row-label">{label}</span>
      <div className="journal-filters-menu__row-content">
        {children}
        {onClear ? (
          <button type="button" className="journal-filters-menu__clear-all" onClick={onClear}>
            Сбросить все
          </button>
        ) : null}
      </div>
    </div>
  );
};

export const FiltersMenuTags = ({
  options,
  value,
  onChange,
  single = false,
}: {
  options: FilterOption[];
  value: string[];
  onChange: (next: string[]) => void;
  single?: boolean;
}) => {
  const toggle = (id: string) => {
    if (id === "ALL") {
      onChange([]);
      return;
    }
    if (single) {
      onChange(value.includes(id) ? [] : [id]);
      return;
    }
    const next = value.includes(id)
      ? value.filter((item) => item !== id)
      : [...value.filter((item) => item !== "ALL"), id];
    onChange(next);
  };

  return (
    <div className="journal-filters-menu__tags">
      {options.map((option) => {
        const active = value.includes(option.id);
        return (
          <button
            key={option.id}
            type="button"
            className={clsx("journal-filters-menu__tag", active && "journal-filters-menu__tag--active")}
            onClick={() => toggle(option.id)}
          >
            <span>{option.label}</span>
            {active && (
              <CloseIcon
                className="journal-filters-menu__tag-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(option.id);
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
};

export const FiltersMenuDate = ({
  value,
  onChange,
  range = false,
  showTime = false,
}: {
  value: [string, string] | string | null;
  onChange: (next: [string, string] | string | null) => void;
  range?: boolean;
  showTime?: boolean;
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const dateFormat = showTime ? "DD.MM.YYYY HH:mm" : "DD.MM.YYYY";

  const displayValue = (() => {
    if (range) {
      const [from, to] = (value as [string, string]) || ["", ""];
      if (from && to) {
        return `${dayjs(from).format(dateFormat)} - ${dayjs(to).format(dateFormat)}`;
      }
      if (from) return dayjs(from).format(dateFormat);
      return "";
    }
    return value ? dayjs(String(value)).format(dateFormat) : "";
  })();

  const clear = () => onChange(range ? ["", ""] : null);

  return (
    <div className="journal-filters-menu__date">
      {displayValue ? (
        <span className="journal-filters-menu__tag journal-filters-menu__tag--active">
          <span>{displayValue}</span>
          <CloseIcon className="journal-filters-menu__tag-remove" onClick={clear} />
        </span>
      ) : null}
      {range ? (
        <DatePicker.RangePicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          format="DD.MM.YYYY"
          value={
            (value as [string, string])?.[0] || (value as [string, string])?.[1]
              ? [
                  (value as [string, string])[0]
                    ? dayjs((value as [string, string])[0])
                    : null,
                  (value as [string, string])[1]
                    ? dayjs((value as [string, string])[1])
                    : null,
                ]
              : null
          }
          onChange={(dates) => {
            onChange([
              dates?.[0]?.format("YYYY-MM-DD") || "",
              dates?.[1]?.format("YYYY-MM-DD") || "",
            ]);
            if (dates?.[0] && dates?.[1]) setPickerOpen(false);
          }}
          style={{ width: 0, height: 0, opacity: 0, padding: 0, border: 0, position: "absolute" }}
        />
      ) : (
        <DatePicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          showTime={showTime ? { format: "HH:mm" } : false}
          format={dateFormat}
          value={value ? dayjs(String(value)) : null}
          onChange={(date) => {
            onChange(date ? date.format(showTime ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD") : null);
            setPickerOpen(false);
          }}
          style={{ width: 0, height: 0, opacity: 0, padding: 0, border: 0, position: "absolute" }}
        />
      )}
      <button
        type="button"
        className="journal-filters-menu__date-action"
        onClick={() => setPickerOpen(true)}
      >
        {displayValue ? "Изменить" : "Добавить +"}
      </button>
    </div>
  );
};

export const FiltersMenuSwitch = ({
  label,
  checked,
  onChange,
  withDivider = true,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  withDivider?: boolean;
}) => (
  <div
    className={clsx(
      "journal-filters-menu__switch-row",
      withDivider && "journal-filters-menu__switch-row--with-divider",
    )}
  >
    <span className="journal-filters-menu__row-label">{label}</span>
    <Switch checked={checked} onChange={onChange} />
  </div>
);

export type JournalActiveFilterChip = {
  key: string;
  filterLabel: string;
  value: string;
  onRemove: () => void;
};

export const formatJournalFilterDate = (value: string) => dayjs(value).format("DD.MM.YYYY");

export const JournalActiveFilterChips = ({ chips }: { chips: JournalActiveFilterChip[] }) => {
  if (!chips.length) return null;

  return (
    <div className="journal-active-filter-chips">
      {chips.map((chip) => (
        <div key={chip.key} className="journal-active-filter-chips__trigger">
          <span className="journal-active-filter-chips__label">{chip.filterLabel}</span>
          <span className="journal-active-filter-chips__value">
            <span className="journal-active-filter-chips__value-text">{chip.value}</span>
            <button
              type="button"
              className="journal-active-filter-chips__clear"
              aria-label={`Удалить фильтр «${chip.filterLabel}»`}
              onClick={chip.onRemove}
            >
              <CloseIcon />
            </button>
          </span>
        </div>
      ))}
    </div>
  );
};
